/**
 * CompanyAdminDashboard.jsx
 * Route: /admin  (for non-system-admin users who are owner or admin of their tenant)
 *
 * Shows:
 *  - Company info + plan
 *  - Team members (users in their tenant) + role management
 *  - Usage stats
 *  - Plan upgrade
 *  - Feature flags (read-only view)
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useDarkMode } from "../../hooks/useDarkMode";
import { useSystemSettings } from "../../hooks/usesystemsettings";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers, FiZap, FiSettings, FiRefreshCw,
  FiCheckCircle, FiAlertCircle, FiShield,
  FiBarChart2, FiCreditCard, FiToggleRight,
} from "react-icons/fi";
import Navigation from "../../components/layout/Navigation";
import UsageProgress from "../../components/plans/UsageProgress";
import PlanCard from "../../components/plans/PlanCard";
import { normaliseRole, ROLE_META } from "../../hooks/useRoles";

/* ── Toast ───────────────────────────────────────────────── */
const Toast = ({ msg, type = "success", onDone }) => {
  useEffect(() => { const id = setTimeout(onDone, 2800); return () => clearTimeout(id); }, [onDone]);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-xl border shadow-2xl text-sm font-medium
        ${type === "success" ? "bg-green-900/80 border-green-500/50 text-green-300" : "bg-red-900/80 border-red-500/50 text-red-300"}`}>
      {type === "success" ? <FiCheckCircle /> : <FiAlertCircle />}{msg}
    </motion.div>
  );
};

/* ── Tabs ────────────────────────────────────────────────── */
const TABS = [
  { id: "team",   labelKey: "users",       icon: <FiUsers /> },
  { id: "usage",  labelKey: "usage_this_month", icon: <FiBarChart2 /> },
  { id: "plans",  labelKey: "plans_title", icon: <FiCreditCard /> },
];

const CompanyAdminDashboard = () => {
  const navigate = useNavigate();
  const { currentUser, userData, tenant, planId } = useAuth();
  const { t, isRTL, lang } = useLanguage();
  const { darkMode } = useDarkMode();

  const [activeTab,    setActiveTab]    = useState("team");
  // Platform-wide flag set by the system admin from /admin. When
  // false we hide the "Plans & pricing" tab here and bounce off it
  // if it's currently active.
  const { settings: sysSettings } = useSystemSettings();
  const plansTabEnabled = sysSettings.plansTabEnabled !== false;
  const visibleTabs = TABS.filter(tb => tb.id !== "plans" || plansTabEnabled);
  useEffect(() => {
    if (!plansTabEnabled && activeTab === "plans") {
      setActiveTab("team");
    }
  }, [plansTabEnabled, activeTab]);
  const [teamMembers,  setTeamMembers]  = useState([]);
  const [plans,        setPlans]        = useState([]);
  const [currentPlan,  setCurrentPlan]  = useState(null);
  const [planUsage,    setPlanUsage]    = useState(null);
  // Track the specific reason usage stats failed to load. Used to
  // render a meaningful inline error in the Usage tab instead of the
  // generic "خطأ" — when this is set we know the request reached the
  // backend and came back with an error message we can show.
  const [usageError,   setUsageError]   = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [actionLoading,setActionLoading]= useState(null);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [toast,        setToast]        = useState(null);

  const showToast = (msg, type = "success") => setToast({ msg, type, id: Date.now() });

  /* ── Shared style helpers ────────────────────────────────── */
  const card       = `border rounded-2xl ${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200 shadow-sm"}`;
  const divider    = darkMode ? "border-gray-700"  : "border-gray-200";
  const theadBg    = darkMode ? "bg-gray-800/80"   : "bg-gray-50";
  const rowHover   = darkMode ? "border-gray-700/60 hover:bg-gray-700/20" : "border-gray-200 hover:bg-gray-50";
  const headingCls = darkMode ? "text-white"       : "text-gray-900";
  const subCls     = darkMode ? "text-gray-400"    : "text-gray-500";
  const textCls    = darkMode ? "text-gray-300"    : "text-gray-700";
  const thCls      = `p-4 font-medium text-sm text-left ${darkMode ? "text-gray-400" : "text-gray-500"}`;
  const refreshBtn = `p-2 rounded-xl transition-colors ${darkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`;

  /* ── Load team members ─────────────────────────────────────
     Uses the getTeamMembers Cloud Function. The previous direct
     Firestore query (`where("tenantId","==",userData.tenantId)`)
     hit the security rules — they only allow a user to read their
     own users/{uid} doc, so the broader query returned a "Missing
     or insufficient permissions" error. Going through a callable
     keeps the rules tight while still letting the team see itself. */
  const loadTeam = useCallback(async () => {
    if (!userData?.tenantId) return;
    try {
      const fn = httpsCallable(functions, "getTeamMembers");
      const res = await fn({ tenantId: userData.tenantId });
      setTeamMembers(res.data?.members || []);
    } catch (err) {
      // Don't show a toast — for a brand-new tenant the function still
      // returns the owner so a real error here is rare. If something
      // does fail, fall back to showing just the current user so the
      // page doesn't render an empty list with no explanation.
      setTeamMembers(userData ? [{
        id: userData.uid,
        email: userData.email,
        displayName: userData.displayName,
        role: userData.role || "owner",
        status: userData.status || "active",
      }] : []);
    }
  }, [userData?.tenantId, userData]);

  /* ── Load usage + plan ─────────────────────────────────────
     Use Promise.allSettled (not Promise.all) so a failure in one
     call doesn't blank the other panel. Previously, if
     getTenantUsageStats threw — for example when a tenant was
     promoted to Pro before the usage block was seeded — the
     whole batch rejected, plans was never set, AND currentPlan
     was never set, leaving BOTH the "this month usage" tab AND
     the "plans" tab empty. Now each result is handled
     independently and the user sees whichever panel succeeded. */
  const loadUsageAndPlans = useCallback(async () => {
    if (!userData?.tenantId) return;
    setUsageError(null); // reset before each fetch
    const [pSettled, uSettled] = await Promise.allSettled([
      httpsCallable(functions, "getAvailablePlans")(),
      httpsCallable(functions, "getTenantUsageStats")({ tenantId: userData.tenantId }),
    ]);

    if (pSettled.status === "fulfilled") {
      const pRes = pSettled.value;
      if (pRes.data?.success) setPlans(pRes.data.plans || []);
    } else {
      // Surface plan-load errors but don't break the rest of the page
      // eslint-disable-next-line no-console
      console.error("getAvailablePlans failed:", pSettled.reason);
      showToast(pSettled.reason?.message || t("error"), "error");
    }

    if (uSettled.status === "fulfilled") {
      const uRes = uSettled.value;
      if (uRes.data?.success) {
        setPlanUsage(uRes.data.usage);
        setCurrentPlan(uRes.data.plan);
      }
    } else {
      // eslint-disable-next-line no-console
      console.error("getTenantUsageStats failed:", uSettled.reason);
      // Capture the error message verbatim so we can render it in the
      // tab rather than the opaque "خطأ" placeholder. Cloud Functions
      // wrap errors in {code, message, details} so we extract the
      // human-readable message field.
      setUsageError(
        uSettled.reason?.message ||
        uSettled.reason?.details?.message ||
        String(uSettled.reason)
      );
      // Soft fallback so the Plans tab can still render even if
      // usage failed: synthesize a minimal currentPlan from the
      // canonical planId on AuthContext. The user won't see the
      // usage progress bars, but the plan cards + price will work.
      if (planId) {
        setCurrentPlan(prev => prev || {
          id: planId,
          name: planId.charAt(0).toUpperCase() + planId.slice(1),
          price: planId === "pro" ? 49 : planId === "enterprise" ? 199 : 0,
        });
      }
      showToast(uSettled.reason?.message || t("error"), "error");
    }
  }, [userData?.tenantId, planId, t]);

  /* ── Initial load ────────────────────────────────────────── */
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadTeam(), loadUsageAndPlans()]);
      setLoading(false);
    };
    init();
  }, [loadTeam, loadUsageAndPlans]);

  /* ── Change role ─────────────────────────────────────────── */
  const handleChangeRole = async (userId, newRole) => {
    setActionLoading(`role-${userId}`);
    try {
      await httpsCallable(functions, "updateUserRole")({ userId, role: newRole });
      setTeamMembers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      showToast(t("role_changed"));
    } catch (err) { showToast(err.message || t("error"), "error"); }
    finally { setActionLoading(null); }
  };

  /* ── Upgrade plan ────────────────────────────────────────── */
  const handleSelectPlan = async (planId) => {
    if (planId === currentPlan?.id) return;
    if (!window.confirm(`${t("confirm_plan_change")} ${planId.toUpperCase()}?`)) return;
    setUpdatingPlan(true);
    try {
      const res = await httpsCallable(functions, "updateTenantPlan")({
        tenantId: userData.tenantId,
        newPlanId: planId,
        oldPlanId: currentPlan?.id,
      });
      if (res.data.success) {
        showToast(`${t("plan_upgraded_success")} ${res.data.plan.name}!`);
        setCurrentPlan(res.data.plan);
        await loadUsageAndPlans();
      }
    } catch (err) { showToast(err.message || t("error"), "error"); }
    finally { setUpdatingPlan(false); }
  };

  /* ── Loading ─────────────────────────────────────────────── */
  if (loading) return (
    <div className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black" : "bg-gradient-to-br from-gray-50 via-white to-gray-100"}`}>
      <Navigation />
      <div className="flex items-center justify-center h-64">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
          <FiRefreshCw className="text-blue-400 text-4xl" />
        </motion.div>
      </div>
    </div>
  );

  const companyName = tenant?.companyName || tenant?.name || userData?.displayName || "—";
  // Prefer the freshly-loaded plan from getTenantUsageStats (so a plan
  // change is reflected immediately), then fall back to the canonical
  // planId on AuthContext, which is itself derived from the tenant doc.
  const planTier    = currentPlan?.id || planId || "free";

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <div dir={isRTL ? "rtl" : "ltr"}
      className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black" : "bg-gradient-to-br from-gray-50 via-white to-gray-100"}`}>
      <Navigation />

      <AnimatePresence>
        {toast && <Toast key={toast.id} msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
      </AnimatePresence>

      <div className="p-4 md:p-8 max-w-6xl mx-auto">

        {/* ── Page header ──────────────────────────────────── */}
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-8">
          <div className="flex items-center gap-3">
            <FiSettings className="text-3xl text-blue-400" />
            <div className="text-left">
              <h1 className={`text-2xl md:text-3xl font-bold ${headingCls}`}>
                {lang === "ar" ? "لوحة تحكم الإدارة" : "Admin Dashboard"}
              </h1>
              <p className={`${subCls} text-sm`}>{companyName}</p>
            </div>
          </div>
        </motion.div>

        {/* ── Company info card ─────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className={`${card} p-6 mb-8`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Company avatar */}
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-2xl font-bold text-white flex-shrink-0">
                {companyName[0]?.toUpperCase()}
              </div>
              <div>
                <h2 className={`text-xl font-bold ${headingCls}`}>{companyName}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium border
                    ${planTier === "pro" ? "bg-blue-500/20 text-blue-400 border-blue-500/40"
                    : planTier === "enterprise" ? "bg-purple-500/20 text-purple-400 border-purple-500/40"
                    : darkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-600 border-gray-200"}`}>
                    {planTier === "pro" ? "💎 Pro"
                     : planTier === "enterprise" ? "⭐ Enterprise"
                     : t("free_plan_name")}
                  </span>
                  <span className={`${subCls} text-sm`}>
                    {lang === "ar" ? "الدور:" : "Your role:"}{" "}
                    <span className="text-blue-400 font-medium">{t(`role_${userData?.role || "member"}`)}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6">
              {[
                { label: lang === "ar" ? "الأعضاء" : "Members", val: teamMembers.length, icon: <FiUsers className="text-blue-400" /> },
                { label: lang === "ar" ? "الخطة" : "Plan", val: currentPlan?.name || "—", icon: <FiZap className="text-yellow-400" /> },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">{s.icon}</div>
                  <p className={`text-xl font-bold ${headingCls}`}>{s.val}</p>
                  <p className={`text-xs ${subCls}`}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ── Tabs ─────────────────────────────────────────── */}
        <div className={`flex gap-1 ${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200"} border rounded-2xl p-1 mb-6 overflow-x-auto`}>
          {visibleTabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap flex-1 justify-center ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white shadow-lg"
                  : darkMode ? "text-gray-400 hover:text-white hover:bg-gray-700/60" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              }`}>
              {tab.icon}<span>{t(tab.labelKey)}</span>
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════
            TAB: TEAM MEMBERS
        ══════════════════════════════════════════════════ */}
        {activeTab === "team" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`${card} overflow-hidden`}>
            <div className={`flex items-center justify-between p-5 border-b ${divider}`}>
              <h2 className={`font-semibold ${headingCls}`}>
                {lang === "ar" ? "أعضاء الفريق" : "Team Members"} ({teamMembers.length})
              </h2>
              <button onClick={loadTeam} className={refreshBtn}><FiRefreshCw className="text-sm" /></button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theadBg}>
                  <tr>
                    <th className={thCls}>{t("user")}</th>
                    <th className={thCls}>{t("role")}</th>
                    <th className={thCls}>{t("col_status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map(u => {
                    const normRole = ROLE_META[normaliseRole(u.role)] ? normaliseRole(u.role) : "viewer";
                    const meta     = ROLE_META[normRole];
                    const isSelf   = u.id === currentUser?.uid;
                    return (
                      <tr key={u.id} className={`border-t ${rowHover} transition-colors`}>
                        <td className="p-4">
                          <p className={`font-medium ${headingCls}`}>
                            {u.displayName || "—"}
                            {isSelf && (
                              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${darkMode ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-600"}`}>
                                {lang === "ar" ? "أنت" : "You"}
                              </span>
                            )}
                          </p>
                          <p className={`${subCls} text-xs`}>{u.email}</p>
                        </td>
                        <td className="p-4">
                          {/* Only owner can change roles, can't change own role */}
                          {userData?.role === "owner" && !isSelf ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={normRole}
                                onChange={e => handleChangeRole(u.id, e.target.value)}
                                disabled={actionLoading === `role-${u.id}`}
                                className={`appearance-none px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer bg-transparent focus:outline-none ${meta?.cls || "bg-gray-500/20 text-gray-400 border-gray-500/40"}`}>
                                {Object.entries(ROLE_META).map(([val, m]) => (
                                  <option key={val} value={val} className={darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"}>
                                    {t(m.labelKey)}
                                  </option>
                                ))}
                              </select>
                              {actionLoading === `role-${u.id}` && <FiRefreshCw className="animate-spin text-blue-400 text-xs" />}
                            </div>
                          ) : (
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${meta?.cls || "bg-gray-500/20 text-gray-400 border-gray-500/40"}`}>
                              {t(meta?.labelKey || `role_${normRole}`)}
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${u.status === "active" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                            {u.status === "active" ? t("status_active") : u.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {teamMembers.length === 0 && (
                    <tr><td colSpan="3" className={`p-12 text-center ${subCls}`}>{t("no_users")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* ══════════════════════════════════════════════════
            TAB: USAGE
        ══════════════════════════════════════════════════ */}
        {activeTab === "usage" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Render the plan header as soon as we know the plan, even
                if the usage stats failed to load. The progress bars
                still gate on planUsage being present below. */}
            {currentPlan && (
              <div className={`${card} p-6`}>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className={`text-lg font-bold ${headingCls}`}>{t("current_usage")}</h2>
                    <p className={`${subCls} text-sm`}>
                      {t("current_plan")}: <span className="text-blue-400 font-medium">{currentPlan.name}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-bold ${headingCls}`}>
                      {currentPlan.price ?? 0}{" "}
                      <span className="text-base font-normal">
                        {/* Currency aware of language: SAR for English,
                            ر.س for Arabic — matches the rest of the app. */}
                        {lang === "ar" ? "ر.س" : "SAR"}
                      </span>
                    </p>
                    <p className={`${subCls} text-sm`}>{t("per_month")}</p>
                  </div>
                </div>
                {planUsage ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <UsageProgress label={t("evaluations")}    current={planUsage.evaluations?.current || 0} quota={planUsage.evaluations?.quota || 0} unit={t("this_month")} showWarning />
                    <UsageProgress label={t("total_campaigns")} current={planUsage.campaigns?.current || 0}  quota={planUsage.campaigns?.quota || 0}   unit={t("total")}      showWarning />
                    <UsageProgress label={t("share_links")}    current={planUsage.shareLinks?.current || 0} quota={planUsage.shareLinks?.quota || 0}  unit={t("total")}      showWarning />
                    <UsageProgress label={t("pdfs_generated")} current={planUsage.pdf?.current || 0}        quota={planUsage.pdf?.quota || 0}          unit={t("this_month")} showWarning />
                  </div>
                ) : (
                  // Diagnostic state when usage fetch failed. Showing
                  // the raw error message + a retry button is much more
                  // useful than the opaque "خطأ" placeholder — the user
                  // (or whoever's debugging) sees exactly which check
                  // failed in getTenantUsageStats.
                  <div className="text-center py-6 space-y-3">
                    <p className="text-red-400 text-sm font-medium">
                      {usageError || t("error")}
                    </p>
                    <button
                      onClick={loadUsageAndPlans}
                      className={`text-xs inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                        darkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-200" : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                      }`}
                    >
                      <FiRefreshCw className="text-xs" /> {t("retry") || (lang === "ar" ? "إعادة المحاولة" : "Retry")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Upgrade CTA if approaching limit */}
            {planUsage && Object.values(planUsage).some(s => s.warning === "warning" || s.warning === "exceeded") && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5 flex items-center justify-between gap-4">
                <div>
                  <p className={`font-semibold ${headingCls}`}>{t("upgrade_to_continue")}</p>
                  <p className={`${subCls} text-sm mt-1`}>{t("approaching_limit")}</p>
                </div>
                <button onClick={() => setActiveTab("plans")}
                  className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors">
                  {t("view_plans")}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* ══════════════════════════════════════════════════
            TAB: PLANS
        ══════════════════════════════════════════════════ */}
        {activeTab === "plans" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  currentPlan={currentPlan}
                  onSelectPlan={handleSelectPlan}
                  loading={updatingPlan}
                  featured={plan.id === "pro"}
                />
              ))}
            </div>

            {/* Billing note */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
              <p className="text-blue-400 text-sm">
                <FiZap className="inline mr-2" />
                <strong>{t("note")}:</strong> {t("mvp_manual_billing")}
              </p>
            </div>
          </motion.div>
        )}

      </div>
    </div>
  );
};

export default CompanyAdminDashboard;